import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Card,
  Typography,
  Switch,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  useTheme,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Stack,
  InputAdornment,
  Alert,
  Tooltip,
  useMediaQuery,
  TextField,
  CircularProgress,
} from '@mui/material';
import {
  Settings as SettingsIcon,
  Search as SearchIcon,
  Business as BusinessIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  ToggleOn as ToggleOnIcon,
  ToggleOff as ToggleOffIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
} from '@mui/icons-material';
import api from '../../utils/api';
import SuccessDialog from '../../components/SuccessDialog';
import './admin-css/ClientManagement-light.css';
import './admin-css/ClientManagement-dark.css';

interface Company {
  _id: string;
  companyName: string;
  companyIndustry: string;
  isActive: boolean;
  features: {
    crm?: boolean;
    [key: string]: boolean | undefined; // Index signature for dynamic feature access
  };
}

const FeaturesManagement: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [companies, setCompanies] = useState<Company[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [toggleAllDialogOpen, setToggleAllDialogOpen] = useState(false);
  const [toggleAllFeature, setToggleAllFeature] = useState<string>('');
  const [toggleAllEnabled, setToggleAllEnabled] = useState(false);
  const [toggling, setToggling] = useState<string | null>(null);
  const [showSuccessDialog, setShowSuccessDialog] = useState(false);
  const [successDialogConfig, setSuccessDialogConfig] = useState({
    title: '',
    message: '',
    autoCloseDelay: 3000,
  });

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage] = useState(10);

  const fetchCompanies = useCallback(async () => {
    try {
      const response = await api.get('/api/companies/features');
      if (response.data.success) {
        setCompanies(response.data.data);
      } else {
        setCompanies([]);
      }
    } catch (error: any) {
      console.error('Error fetching companies with features:', error);
      setCompanies([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCompanies();
  }, [fetchCompanies]);

  const handleToggleFeature = async (companyId: string, featureName: string, currentValue: boolean) => {
    setToggling(`${companyId}-${featureName}`);
    try {
      const response = await api.put(`/api/companies/${companyId}/features/${featureName}`, {
        enabled: !currentValue
      });

      if (response.data.success) {
        // Update local state
        setCompanies(prevCompanies =>
          prevCompanies.map(company =>
            company._id === companyId
              ? {
                  ...company,
                  features: {
                    ...company.features,
                    [featureName]: !currentValue
                  }
                }
              : company
          )
        );

        setSuccessDialogConfig({
          title: 'Feature Updated',
          message: response.data.message,
          autoCloseDelay: 3000,
        });
        setShowSuccessDialog(true);
      }
    } catch (error: any) {
      console.error('Error toggling feature:', error);
      alert('Failed to update feature. Please try again.');
    } finally {
      setToggling(null);
    }
  };

  const handleToggleAllClick = (featureName: string) => {
    setToggleAllFeature(featureName);
    // Check current state - if all are enabled, we'll disable. If any are disabled, we'll enable all.
    const allEnabled = companies.every(company => {
      const features = company.features || {};
      return (features as Record<string, boolean | undefined>)[featureName] !== false;
    });
    setToggleAllEnabled(!allEnabled);
    setToggleAllDialogOpen(true);
  };

  const handleToggleAllConfirm = async () => {
    if (!toggleAllFeature) return;

    setToggling(`all-${toggleAllFeature}`);
    try {
      const response = await api.put(`/api/companies/features/${toggleAllFeature}/toggle-all`, {
        enabled: toggleAllEnabled
      });

      if (response.data.success) {
        // Update all companies
        setCompanies(prevCompanies =>
          prevCompanies.map(company => ({
            ...company,
            features: {
              ...company.features,
              [toggleAllFeature]: toggleAllEnabled
            }
          }))
        );

        setSuccessDialogConfig({
          title: 'All Features Updated',
          message: response.data.message,
          autoCloseDelay: 3000,
        });
        setShowSuccessDialog(true);
        setToggleAllDialogOpen(false);
      }
    } catch (error: any) {
      console.error('Error toggling all features:', error);
      alert('Failed to update all features. Please try again.');
    } finally {
      setToggling(null);
    }
  };

  // Filter companies based on search query
  const filteredCompanies = companies.filter(company =>
    company.companyName.toLowerCase().includes(searchQuery.toLowerCase()) ||
    company.companyIndustry.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Pagination calculations
  const totalItems = filteredCompanies.length;
  const totalPages = Math.ceil(totalItems / rowsPerPage);
  const startIndex = (currentPage - 1) * rowsPerPage;
  const endIndex = Math.min(startIndex + rowsPerPage, totalItems);
  const paginatedCompanies = filteredCompanies.slice(startIndex, endIndex);

  const handlePreviousPage = () => {
    setCurrentPage(prev => Math.max(prev - 1, 1));
  };

  const handleNextPage = () => {
    setCurrentPage(prev => Math.min(prev + 1, totalPages));
  };

  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  const features = [
    {
      name: 'crm',
      label: 'CRM',
      description: 'Customer Relationship Management - Email tracking and lead communication'
    }
    // Add more features here as they're created
  ];

  return (
    <Box
      className={`client-management-container ${theme.palette.mode === 'dark' ? 'dark-mode' : 'light-mode'}`}
      sx={{
        p: 3,
        backgroundColor: theme.palette.custom.background.admin,
        minHeight: '100vh'
      }}>
      {/* Desktop Header */}
      {!isMobile && (
        <>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography
              variant="h2"
              className={`client-management-title ${theme.palette.mode === 'dark' ? 'dark-mode' : 'light-mode'}`}
              sx={{
                fontWeight: 700,
                color: '#202224',
                fontSize: '3rem',
                fontFamily: '"Nunito Sans", sans-serif'
              }}
            >
              Features Management
            </Typography>
          </Box>

          {/* Feature Toggle All Buttons */}
          <Box sx={{ mb: 3, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            {features.map(feature => (
              <Button
                key={feature.name}
                variant="outlined"
                onClick={() => handleToggleAllClick(feature.name)}
                disabled={toggling === `all-${feature.name}` || companies.length === 0}
                startIcon={<SettingsIcon />}
                sx={{
                  borderRadius: '16px',
                  px: 2,
                  py: 1,
                }}
              >
                {toggling === `all-${feature.name}` ? (
                  'Updating...'
                ) : (
                  `Toggle ${feature.label} for All`
                )}
              </Button>
            ))}
          </Box>

          {/* Desktop Search */}
          <Box sx={{ mb: 3 }}>
            <TextField
              fullWidth
              className={`search-field ${theme.palette.mode === 'dark' ? 'dark-mode' : 'light-mode'}`}
              placeholder="Search companies..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
              sx={{ maxWidth: 400 }}
            />
          </Box>
        </>
      )}

      {/* Mobile Header */}
      {isMobile && (
        <Box className={`mobile-header ${theme.palette.mode === 'dark' ? 'dark-mode' : 'light-mode'}`}>
          <Box className={`mobile-header-row ${theme.palette.mode === 'dark' ? 'dark-mode' : 'light-mode'}`}>
            <Typography
              variant="h5"
              className={`client-management-title ${theme.palette.mode === 'dark' ? 'dark-mode' : 'light-mode'}`}
              sx={{
                fontWeight: 600,
                color: '#202224',
                fontSize: '1rem',
                fontFamily: '"Nunito Sans", sans-serif'
              }}
            >
              Features Management
            </Typography>
          </Box>
          <Box className={`mobile-search-row ${theme.palette.mode === 'dark' ? 'dark-mode' : 'light-mode'}`}>
            <TextField
              fullWidth
              className={`search-field ${theme.palette.mode === 'dark' ? 'dark-mode' : 'light-mode'}`}
              placeholder="Search companies..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              size="small"
            />
          </Box>
        </Box>
      )}

      {/* Features Table */}
      {filteredCompanies.length === 0 ? (
        <Card className={`no-companies-card ${theme.palette.mode === 'dark' ? 'dark-mode' : 'light-mode'}`} sx={{ p: 6, textAlign: 'center' }}>
          <BusinessIcon className={`no-companies-icon ${theme.palette.mode === 'dark' ? 'dark-mode' : 'light-mode'}`} sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h6" color="text.secondary" gutterBottom>
            No Companies Found
          </Typography>
        </Card>
      ) : (
        <>
          {/* Desktop Table */}
          {!isMobile && (
            <TableContainer
              component={Paper}
              className={`client-table-container ${theme.palette.mode === 'dark' ? 'dark-mode' : 'light-mode'}`}
              sx={{ borderRadius: 2, boxShadow: 2, backgroundColor: '#FFFFFF' }}
            >
              <Table className={`client-table ${theme.palette.mode === 'dark' ? 'dark-mode' : 'light-mode'}`}>
                <TableHead>
                  <TableRow
                    className={`client-table-header ${theme.palette.mode === 'dark' ? 'dark-mode' : 'light-mode'}`}
                    sx={{ backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.02)' }}
                  >
                    <TableCell className={`client-table-header-cell ${theme.palette.mode === 'dark' ? 'dark-mode' : 'light-mode'}`} sx={{ fontWeight: 'bold', fontSize: '1rem', color: '#667085', fontFamily: 'Inter, sans-serif' }}>Company Name</TableCell>
                    <TableCell className={`client-table-header-cell ${theme.palette.mode === 'dark' ? 'dark-mode' : 'light-mode'}`} sx={{ fontWeight: 'bold', fontSize: '1rem', color: '#667085', fontFamily: 'Inter, sans-serif' }}>Category</TableCell>
                    <TableCell className={`client-table-header-cell ${theme.palette.mode === 'dark' ? 'dark-mode' : 'light-mode'}`} sx={{ fontWeight: 'bold', fontSize: '1rem', color: '#667085', fontFamily: 'Inter, sans-serif' }}>Status</TableCell>
                    {features.map(feature => (
                      <TableCell
                        key={feature.name}
                        className={`client-table-header-cell ${theme.palette.mode === 'dark' ? 'dark-mode' : 'light-mode'}`}
                        sx={{ fontWeight: 'bold', fontSize: '1rem', color: '#667085', fontFamily: 'Inter, sans-serif', textAlign: 'center' }}
                      >
                        {feature.label}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paginatedCompanies.map((company) => (
                    <TableRow
                      key={company._id}
                      className={`client-table-row ${theme.palette.mode === 'dark' ? 'dark-mode' : 'light-mode'}`}
                      sx={{
                        '&:hover': {
                          backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.02)'
                        }
                      }}
                    >
                      <TableCell className={`client-table-cell ${theme.palette.mode === 'dark' ? 'dark-mode' : 'light-mode'}`}>
                        <Typography
                          variant="body1"
                          className={`table-company-name ${theme.palette.mode === 'dark' ? 'dark-mode' : 'light-mode'}`}
                          sx={{ fontWeight: 'medium' }}
                        >
                          {company.companyName}
                        </Typography>
                      </TableCell>
                      <TableCell className={`client-table-cell ${theme.palette.mode === 'dark' ? 'dark-mode' : 'light-mode'}`}>
                        <Typography
                          variant="body2"
                          className={`table-company-industry ${theme.palette.mode === 'dark' ? 'dark-mode' : 'light-mode'}`}
                          color="text.secondary"
                        >
                          {company.companyIndustry}
                        </Typography>
                      </TableCell>
                      <TableCell className={`client-table-cell ${theme.palette.mode === 'dark' ? 'dark-mode' : 'light-mode'}`}>
                        {(() => {
                          const features = company.features || {};
                          const crmEnabled = (features as Record<string, boolean | undefined>).crm !== false;
                          const isActive = company.isActive && crmEnabled;
                          return (
                            <Box
                              className={`status-chip ${isActive ? 'active' : 'inactive'} ${theme.palette.mode === 'dark' ? 'dark-mode' : 'light-mode'}`}
                              sx={{
                                backgroundColor: isActive ? '#14BA6D' : '#F2F4F7',
                                color: isActive ? '#FFFFFF' : '#364254',
                                padding: '4px 8px',
                                borderRadius: '12px',
                                fontSize: '0.75rem',
                                fontWeight: 500,
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: 0.5
                              }}
                            >
                              {isActive ? 'Active' : 'Inactive'}
                            </Box>
                          );
                        })()}
                      </TableCell>
                      {features.map(feature => {
                        const features = company.features || {};
                        const isEnabled = (features as Record<string, boolean | undefined>)[feature.name] !== false; // Default to true if not set
                        const isToggling = toggling === `${company._id}-${feature.name}`;

                        return (
                          <TableCell
                            key={feature.name}
                            align="center"
                            className={`client-table-cell ${theme.palette.mode === 'dark' ? 'dark-mode' : 'light-mode'}`}
                          >
                            {isToggling ? (
                              <CircularProgress size={24} />
                            ) : (
                              <Tooltip title={`${isEnabled ? 'Disable' : 'Enable'} ${feature.label} for ${company.companyName}`}>
                                <IconButton
                                  onClick={() => handleToggleFeature(company._id, feature.name, isEnabled)}
                                  sx={{
                                    color: isEnabled ? '#14BA6D' : '#9CA3AF',
                                    '&:hover': {
                                      backgroundColor: isEnabled ? 'rgba(20, 186, 109, 0.1)' : 'rgba(156, 163, 175, 0.1)'
                                    }
                                  }}
                                >
                                  {isEnabled ? (
                                    <CheckCircleIcon sx={{ fontSize: 28 }} />
                                  ) : (
                                    <CancelIcon sx={{ fontSize: 28 }} />
                                  )}
                                </IconButton>
                              </Tooltip>
                            )}
                          </TableCell>
                        );
                      })}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          {/* Mobile Cards View */}
          {isMobile && (
            <Stack spacing={2}>
              {paginatedCompanies.map((company) => (
                <Card
                  key={company._id}
                  className={`client-card ${theme.palette.mode === 'dark' ? 'dark-mode' : 'light-mode'}`}
                  sx={{ p: 2 }}
                >
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                      {company.companyName}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {company.companyIndustry}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
                    {features.map(feature => {
                      const features = company.features || {};
                      const isEnabled = (features as Record<string, boolean | undefined>)[feature.name] !== false;
                      const isToggling = toggling === `${company._id}-${feature.name}`;

                      return (
                        <Box key={feature.name} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="body2" sx={{ minWidth: 50 }}>
                            {feature.label}:
                          </Typography>
                          {isToggling ? (
                            <CircularProgress size={20} />
                          ) : (
                            <Switch
                              checked={isEnabled}
                              onChange={() => handleToggleFeature(company._id, feature.name, isEnabled)}
                              color="success"
                            />
                          )}
                        </Box>
                      );
                    })}
                  </Box>
                </Card>
              ))}
            </Stack>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 2, mt: 3 }}>
              <IconButton
                onClick={handlePreviousPage}
                disabled={currentPage === 1}
                sx={{
                  backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)',
                  '&:disabled': {
                    opacity: 0.5
                  }
                }}
              >
                <ChevronLeftIcon />
              </IconButton>
              <Typography variant="body2">
                Page {currentPage} of {totalPages}
              </Typography>
              <IconButton
                onClick={handleNextPage}
                disabled={currentPage === totalPages}
                sx={{
                  backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)',
                  '&:disabled': {
                    opacity: 0.5
                  }
                }}
              >
                <ChevronRightIcon />
              </IconButton>
            </Box>
          )}
        </>
      )}

      {/* Toggle All Confirmation Dialog */}
      <Dialog open={toggleAllDialogOpen} onClose={() => setToggleAllDialogOpen(false)}>
        <DialogTitle>Confirm Bulk Update</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            This will {toggleAllEnabled ? 'enable' : 'disable'} the <strong>{features.find(f => f.name === toggleAllFeature)?.label}</strong> feature for <strong>all {companies.length} companies</strong>.
          </Alert>
          <Typography>Are you sure you want to continue?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setToggleAllDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleToggleAllConfirm}
            variant="contained"
            color="primary"
            disabled={toggling !== null}
          >
            {toggling ? 'Updating...' : 'Confirm'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Success Dialog */}
      <SuccessDialog
        open={showSuccessDialog}
        onClose={() => setShowSuccessDialog(false)}
        title={successDialogConfig.title}
        message={successDialogConfig.message}
        autoCloseDelay={successDialogConfig.autoCloseDelay}
      />
    </Box>
  );
};

export default FeaturesManagement;

